import React from 'react';
import { motion } from 'framer-motion';
import { BottleState } from './journeySteps';
import JourneyKey from './JourneyKey';

type JourneyBottleProps = {
  state: BottleState;
  bottleId?: string;
  className?: string;
  capOpen?: boolean;
  unlocking?: boolean;
  capRemoved?: boolean;
  milkVisible?: boolean;
  side?: 'front' | 'back';
  compactBack?: boolean;
  showKey?: boolean;
  keyTurned?: boolean;
  interactiveKey?: boolean;
  onKeyClick?: () => void;
  digitsClassName?: string;
};

const CapLayer = ({ side, bottleId, animateHandle = false }: Pick<JourneyBottleProps, 'side' | 'bottleId'> & { animateHandle?: boolean }) => (
  <>
    <img src="/rightoz-cap-base.svg" alt="" className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-auto w-full" />
    {side !== 'back' && (animateHandle ? (
      <motion.span
        className="absolute left-[28.75%] top-[10.14%] z-[2] h-[6.29%] w-[24.25%] rounded-full border-[3px] border-[#0a4f2c] bg-[#147d48] shadow-[0_2px_3px_rgba(0,0,0,0.25)]"
        style={{ transformOrigin: '100% 50%' }}
        initial={{ rotate: 0 }}
        animate={{ rotate: [0, 0, 90, 90] }}
        transition={{ duration: 3.6, times: [0, 0.48, 0.66, 1], ease: 'easeInOut' }}
      />
    ) : (
      <span className="pointer-events-none absolute left-[28.75%] top-[10.14%] z-[2] h-[6.29%] w-[24.25%] rounded-full border-[3px] border-[#0a4f2c] bg-[#147d48] shadow-[0_2px_3px_rgba(0,0,0,0.25)]" />
    ))}
    {side === 'back' ? (
      <img src="/logo.png" alt="" className="pointer-events-none absolute left-[45.425%] top-[10.5%] z-[2] w-[24.15%]" />
    ) : (
      <span aria-hidden="true" className="pointer-events-none absolute left-[62.5%] top-[9.5%] z-[2] font-sans text-[12cqw] font-black leading-none text-[#202725]">{bottleId}</span>
    )}
  </>
);

const JourneyBottle: React.FC<JourneyBottleProps> = ({
  state,
  bottleId = '7',
  className = '',
  capOpen = false,
  unlocking = false,
  capRemoved = false,
  milkVisible = false,
  side = 'front',
  compactBack = false,
  showKey = false,
  keyTurned = false,
  interactiveKey = false,
  onKeyClick,
  digitsClassName,
}) => {
  const digitsClass = digitsClassName || (compactBack ? 'top-[35%]' : 'top-[34.5%]');
  const digitsMargin = compactBack ? 'mt-0.5 sm:mt-1' : 'mt-1 sm:mt-1.5';

  return (
    <div role="img" aria-label={`RightOZ stainless-steel Bottle #${bottleId}, ${side} side, ${state}`} className={className}>
      <div className="relative [container-type:inline-size]">
        <img src="/rightoz-bottle-007.svg" alt="" className={`block h-auto w-full ${capRemoved || capOpen ? '[clip-path:inset(22%_0_0_0)]' : unlocking ? '[clip-path:inset(21.15%_0_0_0)]' : ''}`} />
        {!capRemoved && !capOpen && !unlocking && <CapLayer side={side} bottleId={bottleId} />}
        {unlocking && (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10"
            initial={{ x: '0%', y: '0%' }}
            animate={{ x: ['0%', '0%', '0%', '0%', '-12%'], y: ['0%', '0%', '0%', '-8%', '-8%'] }}
            transition={{ duration: 3.6, times: [0, 0.66, 0.82, 0.9, 1], ease: 'easeInOut' }}
          >
            <CapLayer side={side} bottleId={bottleId} animateHandle />
          </motion.div>
        )}
        {side === 'front' ? (
          <img src="/logo.png" alt="" className="absolute left-[32.5%] top-[48.6%] w-[35%]" />
        ) : (
          <>
            <span
              aria-hidden="true"
              className={`absolute left-1/2 flex -translate-x-1/2 flex-col items-center text-center text-[24cqw] font-black leading-[0.74] tracking-tight text-[#1e2624] select-none ${digitsClass}`}
            >
              <span>0</span>
              <span className={digitsMargin}>0</span>
              <span className={digitsMargin}>7</span>
            </span>
          </>
        )}
      {(capOpen || milkVisible) && <>
        <motion.span aria-hidden="true" initial={unlocking ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={unlocking ? { delay: 3.15, duration: 0.3 } : undefined} className="pointer-events-none absolute left-[30.625%] top-[16.3%] z-[1] h-[6.5%] w-[38.75%] rounded-[50%] border-[3px] border-[#66716d] bg-[linear-gradient(180deg,#edf0eb_0%,#b4bdb9_58%,#7b8581_100%)] shadow-[inset_0_2px_2px_rgba(255,255,255,0.45),0_2px_5px_rgba(52,67,62,0.24)]" />
        <motion.span aria-hidden="true" initial={unlocking ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={unlocking ? { delay: 3.15, duration: 0.3 } : undefined} className={`absolute left-[33%] top-[17.55%] z-[2] h-[3.2%] w-[34%] rounded-full border shadow-[0_2px_5px_rgba(102,84,42,0.3)] ${milkVisible ? 'border-[#e5d9bd] bg-[#fffdf5]' : 'border-[#66716d] bg-[linear-gradient(#74807b,#d6dcda)]'}`} />
      </>}
      {showKey && !capRemoved && (
        <JourneyKey
          turned={keyTurned}
          interactive={interactiveKey}
          onClick={onKeyClick}
        />
      )}
    </div>
  </div>
  );
};

export default JourneyBottle;
